import React, { useState } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link, useSearchParams } from "react-router-dom";
import Spinner from "../spinner/Spinner";
import Swal from "sweetalert2";
import {
  useAdminPurchases,
  useApproveAdminPurchase,
  useCancelAdminPurchase,
  useSupplyVendorsDropdown,
} from "../../hooks/useInventory";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as CheckCircle } from "bootstrap-icons/icons/check-circle.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import { ReactComponent as PlusSquareFill } from "bootstrap-icons/icons/plus-square-fill.svg";

import formatDate from "../../components/utils/DateFormatter";
const statusColor = (s) => {
  if (s === "approved") return { bg: "#dcfce7", color: "#166534" };
  if (s === "pending") return { bg: "#fef9c3", color: "#854d0e" };
  if (s === "cancelled") return { bg: "#fee2e2", color: "#991b1b" };
  return { bg: "#f3f4f6", color: "#374151" };
};

const AdminPurchaseList = () => {
  const [searchParams] = useSearchParams();
  const presetVendorId = searchParams.get("vendor_id") || "";

  const [search, setSearch] = useState("");
  const [vendorId, setVendorId] = useState(presetVendorId);
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useAdminPurchases({ search, supply_vendor_id: vendorId, status, payment_status: paymentStatus, start_date: startDate, end_date: endDate, page, per_page: 15 });
  const { data: vendorsData } = useSupplyVendorsDropdown();
  const approveMutation = useApproveAdminPurchase();
  const cancelMutation = useCancelAdminPurchase();

  const purchases = data?.data?.purchases || [];
  const summary = data?.data?.summary || {};
  const total = data?.data?.total || 0;
  const totalPages = Math.ceil(total / 15);
  const vendors = vendorsData?.data || [];

  const handleApprove = (p) => {
    Swal.fire({
      title: "Approve Purchase?",
      text: `Approving PO #${p.purchase_number || p.id} will update the warehouse inventory.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, approve it!",
    }).then((result) => {
      if (result.isConfirmed) approveMutation.mutate(p.id);
    });
  };

  const handleCancel = (p) => {
    Swal.fire({
      title: "Cancel Purchase?",
      text: "Provide a reason for cancellation (optional):",
      icon: "warning",
      input: "text",
      inputPlaceholder: "Reason for cancellation...",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, cancel it!",
    }).then((result) => {
      if (result.isConfirmed) cancelMutation.mutate({ purchaseId: p.id, reason: result.value || "" });
    });
  };

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Admin Purchases</h1>
          <Link to="/head-office/inventory/admin-purchases/create" className="bg-orange1 text-white px-4 py-2 rounded hover:bg-orange-600 transition">
            + New Purchase Order
          </Link>
        </div>

        {/* Summary */}
        {summary.total != null && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[
              { label: "Total Amount", value: summary.total_amount },
              { label: "Total Paid", value: summary.total_paid },
              { label: "Outstanding Balance", value: summary.total_balance },
              { label: "Purchases", value: null, count: summary.total },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-lg shadow p-4">
                <p className="text-gray-500 text-xs">{c.label}</p>
                <p className="text-lg font-bold mt-1">
                  {c.count !== undefined ? c.count : <CurrencyFormatter amount={parseFloat((c.value || "0").toString().replace(/,/g, ""))} />}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3">
          <input type="text" placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1 flex-1 min-w-[140px]" />
          <select value={vendorId} onChange={(e) => { setVendorId(e.target.value); setPage(1); }} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1">
            <option value="">All Vendors</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1" />
        </div>

        {isLoading ? (
          <Spinner loading />
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {["Action", "Purchase ID", "Vendor", "Date", "Total Cost", "Amount Paid", "Deficit/Surplus", "Status"].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {purchases.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-center text-gray-500">No purchases found.</td>
                    </tr>
                  ) : (
                    purchases.map((p) => {
                      const sc = statusColor(p.status);
                      // Format datetime for display
                      const formatDateTime = (dateStr) => {
                        if (!dateStr) return "—";
                        return formatDate(dateStr);
                      };
                      return (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-5 py-4 text-sm font-medium whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <Link to={`/head-office/inventory/admin-purchases/show?id=${p.id}`} className="text-orange1 hover:text-orange-600" title="View">
                                <Eye width={18} height={18} />
                              </Link>
                              {p.status === "pending" && (
                                <>
                                  <button onClick={() => handleApprove(p)} disabled={approveMutation.isLoading} className="text-green-600 hover:text-green-800" title="Approve">
                                    <CheckCircle width={18} height={18} />
                                  </button>
                                  <button onClick={() => handleCancel(p)} disabled={cancelMutation.isLoading} className="text-red-500 hover:text-red-700" title="Cancel">
                                    <XCircle width={18} height={18} />
                                  </button>
                                </>
                              )}
                              {p.status === "approved" && (
                                <div className="flex items-center gap-3">
                                  <Link to={`/head-office/inventory/vendor-payments/create?purchase_id=${p.id}`} className="text-blue-600 hover:text-blue-800" title="Add Payment">
                                    <PlusSquareFill width={18} height={18} />
                                  </Link>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-500">{p.purchase_number}</td>
                          <td className="px-5 py-4 text-sm font-medium text-gray-900">{p.vendor?.name || "—"}</td>
                          <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(p.purchase_date)}</td>
                          <td className="px-5 py-4 text-sm font-semibold"><CurrencyFormatter amount={parseFloat((p.total_amount || "0").toString().replace(/,/g, ""))} /></td>
                          <td className="px-5 py-4 text-sm text-green-700"><CurrencyFormatter amount={parseFloat((p.amount_paid || "0").toString().replace(/,/g, ""))} /></td>
                          <td className="px-5 py-4 text-sm font-semibold">
                            <CurrencyFormatter amount={parseFloat((p.balance_due || "0").toString().replace(/,/g, ""))} />
                          </td>
                          <td className="px-5 py-4">
                            <span style={{ padding: "2px 10px", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600, backgroundColor: sc.bg, color: sc.color }}>
                              {p.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <p className="text-sm text-gray-600">Total: {total}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-gray-50">Prev</button>
                  <span className="px-3 py-1 text-sm">{page} / {totalPages}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayouts>
  );
};

export default AdminPurchaseList;
